// 类型安全的i18n hook包装器
// 此文件基于自动生成的类型定义提供类型安全

import { useTranslation } from 'react-i18next';
import {
  TranslationNamespace,
  KeysForNamespace,
  TranslationFunctions,
  UseI18nHook
} from './types/generated';
import { formatNumber as utilsFormatNumber } from './utils';

/**
 * 类型安全的useI18n hook
 *
 * 用法:
 * 1. 指定命名空间: const { t } = useI18n('pages');
 *    t('login.title') // 自动补全pages命名空间的键
 *
 * 2. 不指定命名空间: const { t } = useI18n();
 *    t('pages:login.title') // 使用完整键名
 */
export function useI18n(): TranslationFunctions & { ready: boolean };
export function useI18n<NS extends TranslationNamespace>(namespace: NS): {
  t: (key: KeysForNamespace<NS>, options?: Record<string, any>) => string;
  ready: boolean;
  currentLanguage: string;
  availableLanguages: readonly string[];
  changeLanguage: (lng?: string, callback?: any) => Promise<any>;
  formatNumber: (num: number, options?: Intl.NumberFormatOptions) => string;
};
export function useI18n<NS extends TranslationNamespace>(namespace?: NS) {
  const { t, i18n, ready } = useTranslation(namespace);

  if (namespace) {
    return {
      t: (key: KeysForNamespace<NS>, options?: Record<string, any>) =>
        t(key, { ns: namespace, ...options }),
      ready,
      currentLanguage: i18n.language,
      availableLanguages: i18n.languages || [],
      changeLanguage: i18n.changeLanguage.bind(i18n),
      formatNumber: utilsFormatNumber
    } as {
      t: (key: KeysForNamespace<NS>, options?: Record<string, any>) => string;
      ready: boolean;
      currentLanguage: string;
      availableLanguages: readonly string[];
      changeLanguage: (lng?: string, callback?: any) => Promise<any>;
      formatNumber: (num: number, options?: Intl.NumberFormatOptions) => string;
    };
  }

  return { t, ready } as TranslationFunctions & { ready: boolean };
}

/**
 * 类型安全的翻译键常量
 */
export { TRANSLATION_KEYS, I18nKeys } from './types/generated';

/**
 * 工具函数：构建翻译键
 */
export function buildTranslationKey<NS extends TranslationNamespace>(
  namespace: NS,
  key: KeysForNamespace<NS>
): `${NS}:${KeysForNamespace<NS>}` {
  return `${namespace}:${key}` as const;
}

/**
 * 工具函数：解析翻译键
 */
export function parseTranslationKey(fullKey: string): {
  namespace: TranslationNamespace;
  key: string;
} | null {
  const parts = fullKey.split(':');
  if (parts.length !== 2) {
    return null;
  }

  const [namespace, key] = parts;
  if (!['common', 'pages', 'modules', 'components', 'errors', 'validation'].includes(namespace as TranslationNamespace)) {
    return null;
  }

  return {
    namespace: namespace as TranslationNamespace,
    key
  };
}

// 命名空间常量
export const NAMESPACES = {
  COMMON: 'common' as const,
  PAGES: 'pages' as const,
  MODULES: 'modules' as const,
  COMPONENTS: 'components' as const,
  ERRORS: 'errors' as const,
  VALIDATION: 'validation' as const,
} satisfies Record<string, TranslationNamespace>;

// 类型导出
export type {
  TranslationNamespace,
  TranslationKeys,
  FullTranslationKey,
  KeysForNamespace
} from './types/generated';
